import React, { useState, useEffect, useCallback } from 'react';
import { useBusinessSocials } from '../../../authentication_components/businessSocialsContext';
import { useAdminTracker } from '../../../authentication_components/adminTrackerContext';
import { useAuth } from '../../../authentication_components/authContext';
import {
  Loader2, AlertCircle, CheckCircle, XCircle, X, Save, PlusCircle, Trash2, Eye, EyeOff,
  Instagram, Facebook, Linkedin, Globe, Link2, ExternalLink
} from 'lucide-react';

// X/Twitter icon (lucide doesn't have one yet)
const XTwitterIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

// Helper: generate a socials_id matching the DB constraint ^[A-Za-z0-9\-]+BSOC[0-9]+$
const generateSocialsId = (registryNumber) => {
  const timestamp = Date.now().toString().slice(-6);
  const cleanRegistry = registryNumber.replace(/[^A-Za-z0-9-]/g, '');
  return `${cleanRegistry}-BSOC${timestamp}`;
};

// Platform configuration
const PLATFORMS = [
  {
    key: 'social_instagram',
    label: 'Instagram',
    icon: Instagram,
    placeholder: 'https://instagram.com/tu-negocio',
    color: 'pink',
    bgClass: 'bg-gradient-to-r from-pink-500 to-purple-500',
    textClass: 'text-pink-600',
    hoverClass: 'hover:bg-pink-50',
    borderClass: 'border-pink-200',
    bgLightClass: 'bg-pink-50',
  },
  {
    key: 'social_facebook',
    label: 'Facebook',
    icon: Facebook,
    placeholder: 'https://facebook.com/tu-negocio',
    color: 'blue',
    bgClass: 'bg-blue-600',
    textClass: 'text-blue-600',
    hoverClass: 'hover:bg-blue-50',
    borderClass: 'border-blue-200',
    bgLightClass: 'bg-blue-50',
  },
  {
    key: 'social_linkedin',
    label: 'LinkedIn',
    icon: Linkedin,
    placeholder: 'https://linkedin.com/company/tu-negocio',
    color: 'sky',
    bgClass: 'bg-sky-700',
    textClass: 'text-sky-600',
    hoverClass: 'hover:bg-sky-50',
    borderClass: 'border-sky-200',
    bgLightClass: 'bg-sky-50',
  },
  {
    key: 'social_x',
    label: 'X (Twitter)',
    icon: XTwitterIcon,
    placeholder: 'https://x.com/tu-negocio',
    color: 'gray',
    bgClass: 'bg-gray-900',
    textClass: 'text-gray-800',
    hoverClass: 'hover:bg-gray-50',
    borderClass: 'border-gray-300',
    bgLightClass: 'bg-gray-50',
  },
  {
    key: 'social_website',
    label: 'Sitio Web',
    icon: Globe,
    placeholder: 'https://tu-negocio.com',
    color: 'emerald',
    bgClass: 'bg-emerald-600',
    textClass: 'text-emerald-600',
    hoverClass: 'hover:bg-emerald-50',
    borderClass: 'border-emerald-200',
    bgLightClass: 'bg-emerald-50',
  },
];

const BusinessSocialsDialog = ({ isOpen, onClose, business }) => {
  const {
    getBusinessSocialByRegistry,
    createBusinessSocial,
    updateBusinessSocial,
    deleteBusinessSocial,
    loading: contextLoading,
    error: contextError,
    clearError,
  } = useBusinessSocials();

  const { createTracker } = useAdminTracker();
  const { adminId } = useAuth();

  // State
  const [socialRecord, setSocialRecord] = useState(null);
  const [recordExists, setRecordExists] = useState(false);
  const [initialFetchDone, setInitialFetchDone] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [formData, setFormData] = useState({
    social_visible: false,
    social_instagram: '',
    social_facebook: '',
    social_linkedin: '',
    social_x: '',
    social_website: '',
  });

  const [originalData, setOriginalData] = useState({});
  const [successMessage, setSuccessMessage] = useState('');
  const [localError, setLocalError] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Load existing record
  useEffect(() => {
    if (isOpen && business) {
      setInitialFetchDone(false);
      setSuccessMessage('');
      setLocalError(null);
      setShowDeleteConfirm(false);
      setSocialRecord(null);
      setRecordExists(false);
      clearError();

      const loadSocials = async () => {
        try {
          const result = await getBusinessSocialByRegistry(business.business_registry_number);
          if (result && result.data) {
            const record = result.data;
            setSocialRecord(record);
            setRecordExists(true);

            const loaded = {
              social_visible: record.social_visible || false,
              social_instagram: record.social_instagram || '',
              social_facebook: record.social_facebook || '',
              social_linkedin: record.social_linkedin || '',
              social_x: record.social_x || '',
              social_website: record.social_website || '',
            };
            setFormData(loaded);
            setOriginalData(loaded);
          } else {
            setRecordExists(false);
          }
        } catch (err) {
          if (err.message && (err.message.includes('not found') || err.message.includes('404'))) {
            setRecordExists(false);
            clearError();
          } else {
            setLocalError(err.message);
          }
        } finally {
          setInitialFetchDone(true);
        }
      };

      loadSocials();
    }
  }, [isOpen, business, getBusinessSocialByRegistry, clearError]);

  // Field change handler
  const handleFieldChange = (key, value) => {
    setFormData(prev => ({ ...prev, [key]: value }));
    setSuccessMessage('');
    setLocalError(null);
  };

  // Toggle visibility
  const handleVisibilityToggle = () => {
    setFormData(prev => ({ ...prev, social_visible: !prev.social_visible }));
    setSuccessMessage('');
    setLocalError(null);
  };

  // Check for pending changes
  const hasPendingChanges = useCallback(() => {
    if (!recordExists) return true; // creating new
    return Object.keys(formData).some(key => formData[key] !== originalData[key]);
  }, [formData, originalData, recordExists]);

  // Count filled platforms
  const filledPlatformCount = PLATFORMS.filter(p => formData[p.key]?.trim()).length;

  // CREATE a new record
  const handleCreate = async () => {
    setIsSubmitting(true);
    setLocalError(null);
    setSuccessMessage('');

    try {
      const socialsId = generateSocialsId(business.business_registry_number);

      const payload = {
        socials_id: socialsId,
        business_registry_number: business.business_registry_number,
        social_visible: formData.social_visible,
        social_instagram: formData.social_instagram.trim() || null,
        social_facebook: formData.social_facebook.trim() || null,
        social_linkedin: formData.social_linkedin.trim() || null,
        social_x: formData.social_x.trim() || null,
        social_website: formData.social_website.trim() || null,
      };

      const result = await createBusinessSocial(payload);

      if (result && result.data) {
        setSocialRecord(result.data);
        setRecordExists(true);
        const saved = {
          social_visible: result.data.social_visible || false,
          social_instagram: result.data.social_instagram || '',
          social_facebook: result.data.social_facebook || '',
          social_linkedin: result.data.social_linkedin || '',
          social_x: result.data.social_x || '',
          social_website: result.data.social_website || '',
        };
        setFormData(saved);
        setOriginalData(saved);
      }

      setSuccessMessage('Redes sociales registradas exitosamente.');

      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'business_socials_create',
          change_reason: `Business ${business.business_registry_number} (${business.business_name}) - Social media record created with ${filledPlatformCount} platform(s)`,
        });
      } catch (e) {
        console.error('[AdminTracker] Failed to track socials creation:', e);
      }
    } catch (err) {
      setLocalError(err.message || 'Error al crear registro de redes sociales.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // UPDATE existing record
  const handleUpdate = async () => {
    if (!socialRecord) return;
    setIsSubmitting(true);
    setLocalError(null);
    setSuccessMessage('');

    try {
      const payload = {
        social_visible: formData.social_visible,
        social_instagram: formData.social_instagram.trim() || null,
        social_facebook: formData.social_facebook.trim() || null,
        social_linkedin: formData.social_linkedin.trim() || null,
        social_x: formData.social_x.trim() || null,
        social_website: formData.social_website.trim() || null,
      };

      const result = await updateBusinessSocial(socialRecord.socials_id, payload);

      if (result && result.data) {
        setSocialRecord(result.data);
        const saved = {
          social_visible: result.data.social_visible || false,
          social_instagram: result.data.social_instagram || '',
          social_facebook: result.data.social_facebook || '',
          social_linkedin: result.data.social_linkedin || '',
          social_x: result.data.social_x || '',
          social_website: result.data.social_website || '',
        };
        setFormData(saved);
        setOriginalData(saved);
      }

      setSuccessMessage('Redes sociales actualizadas exitosamente.');

      // Build change description
      const changes = [];
      PLATFORMS.forEach(p => {
        if (formData[p.key] !== originalData[p.key]) {
          const oldVal = originalData[p.key] || '(vacío)';
          const newVal = formData[p.key]?.trim() || '(vacío)';
          changes.push(`${p.label}: "${oldVal}" → "${newVal}"`);
        }
      });
      if (formData.social_visible !== originalData.social_visible) {
        changes.push(`Visibilidad: ${originalData.social_visible ? 'PÚBLICO' : 'PRIVADO'} → ${formData.social_visible ? 'PÚBLICO' : 'PRIVADO'}`);
      }

      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'business_socials_update',
          change_reason: `Business ${business.business_registry_number} (${business.business_name}) - ${changes.join('; ')}`,
        });
      } catch (e) {
        console.error('[AdminTracker] Failed to track socials update:', e);
      }
    } catch (err) {
      setLocalError(err.message || 'Error al actualizar redes sociales.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // DELETE record
  const handleDelete = async () => {
    if (!socialRecord) return;
    setIsSubmitting(true);
    setLocalError(null);
    setSuccessMessage('');

    try {
      await deleteBusinessSocial(socialRecord.socials_id);

      setSocialRecord(null);
      setRecordExists(false);
      setFormData({
        social_visible: false,
        social_instagram: '',
        social_facebook: '',
        social_linkedin: '',
        social_x: '',
        social_website: '',
      });
      setOriginalData({});
      setShowDeleteConfirm(false);
      setSuccessMessage('Registro de redes sociales eliminado.');

      try {
        await createTracker({
          admin_id: adminId || 'unknown',
          change_type: 'business_socials_delete',
          change_reason: `Business ${business.business_registry_number} (${business.business_name}) - Social media record deleted`,
        });
      } catch (e) {
        console.error('[AdminTracker] Failed to track socials deletion:', e);
      }
    } catch (err) {
      setLocalError(err.message || 'Error al eliminar registro.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Save handler (create or update)
  const handleSave = () => {
    if (recordExists) {
      handleUpdate();
    } else {
      handleCreate();
    }
  };

  const isLoading = isSubmitting || contextLoading;
  const displayError = localError || contextError;

  if (!isOpen || !business) return null;

  return (
    <div
      className="fixed inset-0 bg-gray-900 bg-opacity-70 flex items-center justify-center z-[80] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-gray-100 flex justify-between items-center bg-gradient-to-r from-indigo-50 to-purple-50">
          <h3 className="text-lg font-semibold text-gray-800 flex items-center gap-2">
            <Link2 className="h-5 w-5 text-indigo-600" />
            Redes Sociales del Negocio
          </h3>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <XCircle className="h-6 w-6" />
          </button>
        </div>

        {/* Body */}
        <div className="p-6 overflow-y-auto max-h-[calc(90vh-160px)]">
          {/* Business Info Header */}
          <div className="text-center mb-6">
            <h4 className="font-semibold text-gray-900 text-lg">{business.business_name}</h4>
            <p className="text-xs text-gray-500 font-mono mt-1">{business.business_registry_number}</p>
            {recordExists && socialRecord && (
              <p className="text-xs text-gray-400 mt-1">
                ID: <span className="font-mono">{socialRecord.socials_id}</span>
              </p>
            )}
          </div>

          {/* Loading State */}
          {!initialFetchDone ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 text-indigo-500 animate-spin" />
            </div>
          ) : (
            <>
              {/* Visibility Toggle */}
              <div className="mb-6 p-4 rounded-xl border-2 border-gray-200 bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    {formData.social_visible ? (
                      <div className="h-10 w-10 rounded-full bg-green-100 flex items-center justify-center">
                        <Eye className="h-5 w-5 text-green-600" />
                      </div>
                    ) : (
                      <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center">
                        <EyeOff className="h-5 w-5 text-gray-500" />
                      </div>
                    )}
                    <div>
                      <p className="font-semibold text-gray-800">Visibilidad Pública</p>
                      <p className="text-xs text-gray-500">
                        {formData.social_visible
                          ? 'Las redes sociales son visibles al público'
                          : 'Las redes sociales están ocultas del público'}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={handleVisibilityToggle}
                    disabled={isLoading}
                    className={`relative inline-flex h-7 w-12 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 disabled:opacity-50 ${
                      formData.social_visible ? 'bg-green-600' : 'bg-gray-300'
                    }`}
                  >
                    <span
                      className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform shadow-sm ${
                        formData.social_visible ? 'translate-x-6' : 'translate-x-1'
                      }`}
                    />
                  </button>
                </div>
              </div>

              {/* Platform Count Badge */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-medium text-gray-700">Plataformas</span>
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-bold ${
                    filledPlatformCount > 0 ? 'bg-indigo-100 text-indigo-700' : 'bg-gray-100 text-gray-500'
                  }`}>
                    {filledPlatformCount} / {PLATFORMS.length}
                  </span>
                </div>
                {recordExists && (
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    disabled={isLoading}
                    className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-red-600 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                    Eliminar Registro
                  </button>
                )}
              </div>

              {/* Platform Inputs */}
              <div className="space-y-3">
                {PLATFORMS.map((platform) => {
                  const Icon = platform.icon;
                  const hasValue = formData[platform.key]?.trim();

                  return (
                    <div
                      key={platform.key}
                      className={`rounded-xl border-2 transition-all ${
                        hasValue ? `${platform.borderClass} ${platform.bgLightClass}` : 'border-gray-200 bg-white'
                      }`}
                    >
                      <div className="p-4">
                        <div className="flex items-center gap-3 mb-2">
                          <div className={`h-8 w-8 rounded-lg flex items-center justify-center ${
                            hasValue ? platform.bgClass : 'bg-gray-200'
                          }`}>
                            <Icon className={`h-4 w-4 ${hasValue ? 'text-white' : 'text-gray-500'}`} />
                          </div>
                          <label className="font-medium text-gray-800 text-sm">
                            {platform.label}
                          </label>
                          {hasValue && (
                            <a
                              href={formData[platform.key]}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={`ml-auto ${platform.textClass} hover:opacity-70 transition-opacity`}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-4 w-4" />
                            </a>
                          )}
                        </div>
                        <div className="flex gap-2">
                          <input
                            type="url"
                            value={formData[platform.key]}
                            onChange={(e) => handleFieldChange(platform.key, e.target.value)}
                            disabled={isLoading}
                            placeholder={platform.placeholder}
                            className={`flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-400 disabled:bg-gray-100 disabled:text-gray-400 ${
                              hasValue ? 'border-gray-300' : 'border-gray-200'
                            }`}
                          />
                          {hasValue && (
                            <button
                              onClick={() => handleFieldChange(platform.key, '')}
                              disabled={isLoading}
                              className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-50"
                              title="Limpiar"
                            >
                              <X className="h-4 w-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Delete Confirmation */}
              {showDeleteConfirm && (
                <div className="mt-4 p-4 bg-red-50 border-2 border-red-300 rounded-xl">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="font-semibold text-red-800 text-sm">¿Eliminar todas las redes sociales?</p>
                      <p className="text-xs text-red-700 mt-1">
                        Se eliminará el registro completo de redes sociales de este negocio. Esta acción no se puede deshacer.
                      </p>
                      <div className="flex gap-2 mt-3">
                        <button
                          onClick={handleDelete}
                          disabled={isLoading}
                          className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center gap-1"
                        >
                          {isLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                          Sí, Eliminar
                        </button>
                        <button
                          onClick={() => setShowDeleteConfirm(false)}
                          disabled={isLoading}
                          className="px-4 py-2 border border-gray-300 text-sm font-medium rounded-lg hover:bg-white transition-colors"
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Error Display */}
              {displayError && (
                <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2 text-sm">
                  <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-red-800">Error</p>
                    <p className="text-red-700">{displayError}</p>
                  </div>
                </div>
              )}

              {/* Success Display */}
              {successMessage && (
                <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center gap-2 text-sm">
                  <CheckCircle className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <p className="text-green-800 font-medium">{successMessage}</p>
                </div>
              )}
            </>
          )}
        </div>

        {/* Footer */}
        {initialFetchDone && (
          <div className="p-4 border-t border-gray-200 bg-gray-50 flex gap-3">
            <button
              onClick={onClose}
              disabled={isLoading}
              className="flex-1 px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-white transition-colors disabled:opacity-50 font-medium"
            >
              {successMessage ? 'Cerrar' : 'Cancelar'}
            </button>
            <button
              onClick={handleSave}
              disabled={isLoading || !hasPendingChanges()}
              className="flex-1 px-4 py-2.5 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-sm"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  {recordExists ? 'Guardando...' : 'Creando...'}
                </>
              ) : (
                <>
                  {recordExists ? <Save className="h-4 w-4" /> : <PlusCircle className="h-4 w-4" />}
                  {recordExists ? 'Guardar Cambios' : 'Crear Registro'}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default BusinessSocialsDialog;